import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    Check, Star, Zap, Crown, Clock, Shield, Users
} from 'lucide-react'
import Button from '../components/Button'
import { useAuth } from '../contexts/AuthContext'
import { useTrialStatus } from '../hooks/useTrialStatus'
import { supabase } from '../services/supabase'
import {
    PERIODICIDADE_SUFIXO_PRECO,
    normalizarPeriodicidade,
    obterValorCobrancaPlano,
} from '../utils/periodicidadePix'

interface Plano {
    id: string
    nome: string
    nome_display: string
    descricao: string | null
    preco_mensal: number
    preco_anual: number | null
    periodicidade?: string | null
    periodo_trial: number
    recursos: string[]
    limite_vendas?: number
    limite_corretores?: number
    limite_imoveis?: number
    suporte_prioritario: boolean
    relatorios_avancados: boolean
    api_access: boolean
    ordem_exibicao: number
}

interface AfiliadoInfo {
    id: string
    codigo_afiliado: string
    nome_corretor: string
}

export default function Planos() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { user } = useAuth()
    const { hasAnySubscription, subscriptionStatus, currentPlan } = useTrialStatus()
    const [planos, setPlanos] = useState<Plano[]>([])
    const [afiliadoInfo, setAfiliadoInfo] = useState<AfiliadoInfo | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [assinando, setAssinando] = useState<string | null>(null)

    useEffect(() => {
        loadPlanos()
        checkAfiliado()
    }, [])

    const loadPlanos = async () => {
        try {
            const { data, error } = await supabase
                .from('planos')
                .select('*')
                .eq('status', 'ativo')
                .order('ordem_exibicao', { ascending: true })

            if (error) {
                throw error
            }

            const planosProcessados = data?.map(plano => ({
                ...plano,
                recursos: Array.isArray(plano.recursos) ? plano.recursos : []
            })) || []

            setPlanos(planosProcessados)
        } catch (err) {
            setError(`Erro ao carregar planos disponíveis: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
        } finally {
            setLoading(false)
        }
    }

    const checkAfiliado = async () => {
        const refCode = searchParams.get('ref')
        if (!refCode) return

        try {
            const { data: afiliado, error } = await supabase
                .from('afiliados')
                .select(`
                    id,
                    codigo_afiliado,
                    corretores!inner (
                        nome
                    )
                `)
                .eq('codigo_afiliado', refCode)
                .single()

            if (error || !afiliado) {
                console.log('Afiliado não encontrado:', refCode)
                return
            }

            setAfiliadoInfo({
                id: afiliado.id,
                codigo_afiliado: afiliado.codigo_afiliado,
                nome_corretor: (afiliado.corretores as any).nome
            })
        } catch (err) {
            console.error('Erro ao verificar afiliado:', err)
        }
    }

    const handleAssinatura = async (planoId: string) => {
        if (!user) {
            navigate('/login')
            return
        }

        // Verificar se já tem QUALQUER assinatura antes de tentar criar
        if (hasAnySubscription) {
            // Redirecionar para página de assinatura em vez de mostrar modal
            navigate('/assinar')
            return
        }

        setAssinando(planoId)
        try {
            const { error } = await supabase.rpc('create_assinatura_trial', {
                p_user_id: user.id,
                p_plano_id: planoId,
                p_afiliado_id: afiliadoInfo?.id || null
            })

            if (error) {
                throw error
            }

            // Redirecionar para dashboard com sucesso
            navigate('/dashboard?assinatura=sucesso')
        } catch (err) {
            console.error('Erro ao criar assinatura:', err)
            const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
            setError(`Erro ao criar assinatura: ${errorMessage}`)
        } finally {
            setAssinando(null)
        }
    }

    const handleContinuarPlano = (planoId: string) => {
        // Redirecionar para página de pagamento com o plano específico
        navigate(`/pagamento?plano=${planoId}`)
    }

    const isCurrentPlan = (planoId: string): boolean => {
        return currentPlan?.plano_id === planoId
    }

    const isExpiredPlan = (): boolean => {
        return subscriptionStatus === 'assinatura_expirada'
    }

    const getPlanoIcon = (nome: string) => {
        switch (nome) {
            case 'start':
                return <Star className="w-12 h-12 text-blue-500" />
            case 'medium':
                return <Zap className="w-12 h-12 text-purple-500" />
            case 'pro':
                return <Crown className="w-12 h-12 text-yellow-500" />
            default:
                return <Star className="w-12 h-12 text-gray-500" />
        }
    }

    const getPlanoColor = (nome: string) => {
        switch (nome) {
            case 'start':
                return 'border-blue-200 dark:border-blue-700'
            case 'medium':
                return 'border-purple-200 dark:border-purple-700'
            case 'pro':
                return 'border-yellow-200 dark:border-yellow-700'
            default:
                return 'border-gray-200 dark:border-gray-700'
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Escolha seu Plano
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-2">
                            Comece gratuitamente por 7 dias, cancele quando quiser
                        </p>
                        {afiliadoInfo && (
                            <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-lg">
                                <Users className="w-4 h-4" />
                                <span className="text-sm">
                                    Indicado por: {afiliadoInfo.nome_corretor}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>


            {/* Error Message */}
            {error && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-red-800 dark:text-red-400">{error}</p>
                    </div>
                </div>
            )}

            {/* Planos */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {planos.map((plano) => (
                        <div
                            key={plano.id}
                            className={`bg-white dark:bg-gray-800 rounded-2xl border-2 ${getPlanoColor(plano.nome)} p-8 relative transition-all duration-300 hover:shadow-xl ${plano.nome === 'pro' ? 'scale-105' : ''}`}
                        >
                            {/* Badge para plano Pro */}
                            {plano.nome === 'pro' && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1">
                                        <Crown className="w-4 h-4" />
                                        Mais Popular
                                    </div>
                                </div>
                            )}

                            {/* Header do Plano */}
                            <div className="text-center mb-8">
                                <div className="flex justify-center mb-4">
                                    {getPlanoIcon(plano.nome)}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                    {plano.nome_display}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-4">
                                    {plano.descricao}
                                </p>
                                <div className="mb-6">
                                    <div className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                                        {formatCurrency(obterValorCobrancaPlano(plano))}
                                        <span className="text-lg font-normal text-gray-500 dark:text-gray-400">
                                            {PERIODICIDADE_SUFIXO_PRECO[normalizarPeriodicidade(plano.periodicidade)]}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center gap-2 text-sm text-blue-600 dark:text-blue-400 mb-6">
                                    <Clock className="w-4 h-4" />
                                    <span>{plano.periodo_trial} dias grátis</span>
                                </div>
                            </div>

                            {/* Recursos do Plano */}
                            <div className="space-y-4 mb-8">
                                {Array.isArray(plano.recursos) && plano.recursos.map((recurso: string, index: number) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        <span className="text-gray-700 dark:text-gray-300">{recurso}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Botão de Ação */}
                            {isExpiredPlan() && isCurrentPlan(plano.id) ? (
                                // Botão especial para plano atual expirado
                                <>
                                    <div className="mb-3 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <span className="text-xs font-medium text-yellow-800">Seu Plano Atual</span>
                                    </div>
                                    <Button
                                        variant="btn_add"
                                        onClick={() => handleContinuarPlano(plano.id)}
                                        className="w-full"
                                    >
                                        Continuar com meu plano →
                                    </Button>
                                </>
                            ) : (
                                // Botões normais
                                <Button
                                    variant={plano.nome === 'pro' ? 'btn_add' : 'btn_secondary'}
                                    onClick={() => handleAssinatura(plano.id)}
                                    loading={assinando === plano.id}
                                    disabled={hasAnySubscription || (assinando !== null && assinando !== plano.id)}
                                    className="w-full"
                                >
                                    {hasAnySubscription
                                        ? 'Assinar'
                                        : assinando === plano.id
                                            ? 'Processando...'
                                            : 'Começar Teste Grátis →'
                                    }
                                </Button>
                            )}

                            {/* Garantia */}
                            <div className="mt-4 text-center">
                                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Shield className="w-4 h-4" />
                                    <span>Cancelamento em 7 dias</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* FAQ ou informações adicionais */}
                <div className="mt-16 text-center">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
                        Perguntas Frequentes
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                Como funciona o período de teste?
                            </h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Você tem 7 dias completos para testar todos os recursos do plano escolhido.
                                Não é necessário cartão de crédito para começar.
                            </p>
                        </div>
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                Posso cancelar a qualquer momento?
                            </h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Sim! Você pode cancelar sua assinatura a qualquer momento,
                                sem taxas ou multas.
                            </p>
                        </div>
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                Como funcionam as comissões para afiliados?
                            </h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Afiliados recebem 10% de comissão sobre cada assinatura ativa
                                que indicaram, pagos mensalmente.
                            </p>
                        </div>
                        <div className="text-left">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                Posso mudar de plano depois?
                            </h4>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                Sim! Você pode fazer upgrade ou downgrade do seu plano
                                a qualquer momento.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}